/**
 * C1 (Persona-Umbau 28.10.2026): drei Kacheln mit Richtung unter dem Seitentitel. Jede liest DIESELBE Antwort wie
 * die Karte darunter (geteilte Abrufe in `helpers/umsatzData.ts`) und nennt ihr Fenster im Etikett:
 * - Bezahlquote 12 Monate gegen dieselbe reife Spanne ein Jahr früher = „Alle Anfos" in „Wer bestellt — und wer zahlt", ▲/▼ aus
 *   demselben Zwei-Anteile-Test;
 * - Anfos im laufenden Quartal gegen dasselbe Fenster ein Jahr früher = Quartalsgrafik in „Dieses Jahr gegen die
 *   Vorjahre" (`quartale_verlauf`), ▲/▼ aus dem Test zweier Zählwerte (`zaehlerZ`);
 * - Zahlungseingang dieses Quartals gegen Plan = Kachel in „Die nächste Rechnung …".
 * Ohne Daten: „—" mit Grund, kein Absturz.
 */
import { FC, ReactNode } from 'react';
import { Box, Link, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import InfoTip from '../../../components/InfoTip';
import { Kachel, KACHEL_RASTER, int, useLaden } from '../../../components/auswertungen/kennzahlTeile';
import { formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { formatPeriode } from '../../../helpers/auswertungenData';
import { fetchKennzahlenGeteilt, fetchKundentypGeteilt, fetchZahlungseingangGeteilt } from '../../../helpers/umsatzData';
import { anfosKopf, bezahlquoteKopf, bisTag, delta, deltaPp, Marke, planStatus, reifEtikett, spanneKurz } from './befund';

/** Pfeil und Veränderung; Farbe nur, wenn der Test trennt (ohne Marke grau „kann Zufall sein"). */
export const Richtung: FC<{ marke: Marke; text: string; gutBei: 'up' | 'down'; tip: string }> = ({ marke, text, gutBei, tip }) => {
  const farbe = marke === null ? 'text.secondary' : marke === gutBei ? 'success.main' : 'error.main';
  return (
    <Tooltip title={tip} arrow placement="top">
      <Box component="span" sx={{ color: farbe, fontSize: 16, fontWeight: 700, ml: 1, whiteSpace: 'nowrap', cursor: 'help' }}>
        {marke === 'down' ? '▼ ' : marke === 'up' ? '▲ ' : ''}
        {text}
      </Box>
    </Tooltip>
  );
};

/** Status gegen Plan: Pfeil + Wort + Farbe (Status nie nur über Farbe). */
export const PlanMarke: FC<{ drift: number | null | undefined }> = ({ drift }) => {
  const st = planStatus(drift);
  if (!st) return null;
  const farbe = st.ton === 'schlecht' ? 'error.main' : st.ton === 'gut' ? 'success.main' : 'text.secondary';
  return (
    <Box component="span" sx={{ color: farbe, fontSize: 16, fontWeight: 700, ml: 1, whiteSpace: 'nowrap' }}>
      {st.ton === 'schlecht' ? '▼ ' : st.ton === 'gut' ? '▲ ' : ''}
      {st.text}
    </Box>
  );
};

const testSatz = (marke: Marke) =>
  marke === null
    ? 'Ohne ▲/▼: Der Test über beide Zeiträume trennt den Unterschied nicht vom Zufall.'
    : '▲/▼: Der Test über beide Zeiträume trennt den Unterschied vom Zufall.';

const Strich: FC<{ grund: string }> = ({ grund }) => (
  <Tooltip title={grund} arrow placement="top">
    <Box component="span" sx={{ cursor: 'help' }}>
      —
    </Box>
  </Tooltip>
);

const laden = (fehler: string | null): ReactNode => (fehler ? <Strich grund={`Konnte nicht geladen werden: ${fehler}`} /> : '…');

const QuoteKachel: FC = () => {
  const { data, fehler } = useLaden(fetchKundentypGeteilt);
  const k = bezahlquoteKopf(data?.summary);
  // Audit B2 (30.10.): Das Etikett nennt das echte reife Fenster („Anfos 31.10.25–31.08.26, ab 60 Tagen"), die Unterzeile
  // die Spanne davor — „12 Monate bis 29.10." las sich, als zählten die jüngsten zwei Monate mit. Ohne Fenster wie früher.
  const reif = data ? reifEtikett(data.window_reif, data.reife_tage) : null;
  const etikett = reif ?? (data ? `12 Monate bis ${bisTag(data.window.bis)}` : '');
  // Die Quote davor rechnet über dieselbe reife Spanne ein Jahr früher (Plan 2026-10-30-vergleichsfenster-drift).
  const davorSpanne = data?.window_davor_reif ? spanneKurz(data.window_davor_reif.erster_tag, data.window_davor_reif.letzter_tag) : null;
  return (
    <Kachel
      wert={
        !data ? (
          laden(fehler)
        ) : !k ? (
          <Strich grund="Für dieselbe Spanne ein Jahr früher liegt keine Bezahlquote vor." />
        ) : (
          <>
            {formatPct(k.jetzt)}
            <Richtung marke={k.marke} text={deltaPp(k.diffPp)} gutBei="up" tip={`${testSatz(k.marke)} ${TERMS.abweichungZeitraumMarke.tip}`} />
          </>
        )
      }
      label={`${TERMS.bezahlquote.label}, ${etikett}`}
      tip={TERMS.bezahlquote.tip}
      sub={
        k && data ? (
          <>
            gegen {formatPct(k.davor)} {davorSpanne ? `in derselben Spanne ein Jahr früher (${davorSpanne})` : 'ein Jahr früher'} · {int(data.summary.mature_paid)} von{' '}
            {int(data.summary.mature_anfos)} Anfos haben gezahlt ·{' '}
            {/* Router-Link mit Anker: funktioniert auf der Kapitelseite UND auf der Übersicht („Lage", Runde 2). */}
            <Link component={RouterLink} to="/auswertungen/umsatz#wer-bestellt-wer-zahlt" underline="hover">
              wer nicht zahlt →
            </Link>
          </>
        ) : undefined
      }
    />
  );
};

const AnfosKachel: FC = () => {
  const { data, fehler } = useLaden(fetchKennzahlenGeteilt);
  const k = anfosKopf(data?.quartale_verlauf);
  return (
    <Kachel
      wert={
        !data ? (
          laden(fehler)
        ) : !k ? (
          <Strich grund="Kein Vorjahresquartal im Datenbestand." />
        ) : (
          <>
            {delta(k.delta)}
            <Richtung
              marke={k.marke}
              text={k.marke === null ? 'kann Zufall sein' : ''}
              gutBei="up"
              tip={`${testSatz(k.marke)} Verglichen werden die Anfo-Zahlen zweier gleich langer Fenster; den Rechenweg nennt „Wie diese Sektion rechnet“ auf der Übersicht.`}
            />
          </>
        )
      }
      label={k ? `${TERMS.anfos.label} ${k.fenster}` : `${TERMS.anfos.label} im laufenden Quartal gegen das Vorjahr`}
      tip={`${TERMS.anfos.tip} Ohne vorläufig ergänzte. Das Quartal endet am Datenstand des Bestellsystems, das Vorjahr am selben Tag zur selben Uhrzeit.`}
      sub={
        k ? (
          <>
            {int(k.jetzt)} gegen {int(k.basis)} Anfos · je Quartal:{' '}
            <Link component={RouterLink} to="/auswertungen/umsatz#dieses-jahr-gegen-vorjahre" underline="hover">
              Grafik „Dieses Jahr gegen die Vorjahre“ →
            </Link>
          </>
        ) : undefined
      }
    />
  );
};

const PlanKachel: FC = () => {
  const { data, fehler } = useLaden(fetchZahlungseingangGeteilt);
  return (
    <Kachel
      wert={
        !data ? (
          fehler ? (
            laden(fehler)
          ) : (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              …
            </Box>
          )
        ) : data.drift_pct === null ? (
          <Strich grund="Das Quartal hat gerade begonnen." />
        ) : (
          <>
            {delta(data.drift_pct, 1)}
            <PlanMarke drift={data.drift_pct} />
          </>
        )
      }
      label={`${TERMS.zahlungseingangGegenPlan.label}${data ? ` (${formatPeriode(data.quarter)})` : ''}`}
      tip={TERMS.zahlungseingangGegenPlan.tip}
      sub={
        !data ? (
          fehler ? 'konnte nicht geladen werden' : 'wird geladen (rechnet einige Sekunden)'
        ) : (
          <>
            {data.drift_pct === null ? 'Quartal hat gerade begonnen' : `nach ${int(data.completed_weeks)} von ${int(data.total_weeks)} Wochen`} · Plan nach
            erwarteten Ausfällen ·{' '}
            <Link component={RouterLink} to="/auswertungen/umsatz/cashflow?tab=history" underline="hover">
              Cashflow →
            </Link>
            {data.stichtag_hinweis && <InfoTip text={data.stichtag_hinweis} />}
          </>
        )
      }
    />
  );
};

const KopfKacheln: FC = () => (
  <Box sx={{ ...KACHEL_RASTER, mb: 3 }}>
    <QuoteKachel />
    <AnfosKachel />
    <PlanKachel />
  </Box>
);

export default KopfKacheln;
